import React, { useEffect, useState } from 'react';
import { Mail, Send, Eye, AlertCircle, CheckCircle2, History } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import RichTextEditor from '../../components/RichTextEditor';

const AUDIENCES = [
  { value: 'all', label: 'Todos los miembros activos' },
  { value: 'current', label: 'Solo al corriente' },
  { value: 'expired', label: 'Solo vencidos' },
];

export default function AdminNewsletterPage() {
  const [form, setForm] = useState({ subject: '', html: '', audience: 'all' });
  const [previewInfo, setPreviewInfo] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    api.get('/admin/newsletter/log').then(({ data }) => setLog(data || [])).catch(() => {});
  }, []);

  async function doPreview() {
    setPreviewing(true); setFeedback(null);
    try {
      const { data } = await api.post('/admin/newsletter/preview', form);
      setPreviewInfo(data);
    } catch (err) { setFeedback({ type: 'error', msg: formatApiError(err.response?.data?.detail) }); }
    setPreviewing(false);
  }

  async function doSend() {
    if (!previewInfo) { await doPreview(); return; }
    if (!window.confirm(`¿Enviar a ${previewInfo.count} destinatarios? Esta acción no se puede deshacer.`)) return;
    setSending(true); setFeedback(null);
    try {
      const { data } = await api.post('/admin/newsletter/send', form);
      setFeedback({ type: 'success', msg: `✉ Enviados ${data.sent} de ${data.total_targets}${data.failed ? ` (${data.failed} fallidos)` : ''}` });
      const { data: log2 } = await api.get('/admin/newsletter/log');
      setLog(log2 || []);
    } catch (err) { setFeedback({ type: 'error', msg: formatApiError(err.response?.data?.detail) }); }
    setSending(false);
  }

  return (
    <div data-testid="admin-newsletter-page" className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Mail size={22} />Newsletter</h1>
        <p className="text-charcoal-500 text-sm mt-1">Envía correos masivos a los miembros (usa la configuración de Brevo).</p>
      </header>

      {feedback && (
        <div data-testid="newsletter-feedback" className={`rounded-xl p-4 text-sm flex items-start gap-2 ${feedback.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-rose-50 border border-rose-200 text-rose-800'}`}>
          {feedback.type === 'success' ? <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />}
          <span>{feedback.msg}</span>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Asunto</label>
          <input
            value={form.subject}
            onChange={(e) => { setForm({ ...form, subject: e.target.value }); setPreviewInfo(null); }}
            data-testid="newsletter-subject"
            placeholder="Ej: Recordatorio: próxima asamblea del CMIC"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Audiencia</label>
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map((a) => (
              <button key={a.value} type="button" onClick={() => { setForm({ ...form, audience: a.value }); setPreviewInfo(null); }} data-testid={`newsletter-audience-${a.value}`} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${form.audience === a.value ? 'bg-cmic-500 text-white' : 'bg-white border border-gray-200 text-charcoal-600 hover:bg-gray-50'}`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Contenido</label>
          <RichTextEditor
            value={form.html}
            onChange={(html) => { setForm({ ...form, html }); setPreviewInfo(null); }}
            placeholder="Hola colega, te recordamos que…"
            testid="newsletter-html"
            minHeight={280}
          />
          <p className="text-[11px] text-charcoal-400 mt-1">Puedes incluir negritas, listas, links e imágenes. Las imágenes se subirán automáticamente al servidor cuando guardes.</p>
        </div>

        {previewInfo && (
          <div className="bg-cmic-50 border border-cmic-100 rounded-xl p-4 text-sm" data-testid="newsletter-preview-info">
            <div className="flex items-center gap-2 mb-2 font-semibold text-cmic-800"><Eye size={14} />Vista previa de envío</div>
            <p className="text-cmic-700">Se enviará a <strong>{previewInfo.count}</strong> destinatario{previewInfo.count === 1 ? '' : 's'}.</p>
            {previewInfo.sample?.length > 0 && (
              <ul className="mt-2 text-xs text-charcoal-600 space-y-0.5">
                {previewInfo.sample.map((s, i) => <li key={i}>· {s.full_name} ({s.email})</li>)}
                {previewInfo.count > previewInfo.sample.length && <li className="text-charcoal-400">…y {previewInfo.count - previewInfo.sample.length} más</li>}
              </ul>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <button onClick={doPreview} disabled={previewing || !form.subject.trim() || !form.html.trim()} data-testid="newsletter-preview-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
            <Eye size={14} />{previewing ? 'Calculando…' : 'Previsualizar'}
          </button>
          <button onClick={doSend} disabled={sending || !previewInfo || previewInfo.count === 0} data-testid="newsletter-send-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50">
            {sending ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={14} />}{sending ? 'Enviando…' : 'Enviar a todos'}
          </button>
        </div>
      </div>

      {log.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="newsletter-log">
          <div className="flex items-center gap-2 mb-3"><History size={14} className="text-charcoal-600" /><h3 className="font-semibold text-charcoal-800 text-sm">Envíos recientes</h3></div>
          <ul className="divide-y divide-gray-100">
            {log.map((e) => (
              <li key={e.id} className="py-3 flex items-start gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-charcoal-800 truncate">{e.subject}</div>
                  <div className="text-[11px] text-charcoal-500">{new Date(e.created_at).toLocaleString('es-MX')} · {AUDIENCES.find((a) => a.value === e.audience)?.label || e.audience}</div>
                </div>
                <div className="text-right text-xs flex-shrink-0">
                  <div className="font-semibold text-emerald-700">{e.sent} enviados</div>
                  {e.failed > 0 && <div className="text-rose-600">{e.failed} fallidos</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
